import { Grid } from '@factorialco/deprecated-design-system'
import {
  ContractVersionPageContractVersionFragment,
  WorkingConditionVersionApplicationFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import centsToDecimal from 'modules/core/lib/formatCents'
import i18n from 'modules/core/lib/i18n'

import Section from 'modules/contracts/components/ContractDetails/Section'
import BlankSection from 'modules/contracts/components/ContractDetails/Section/Blank'

import AnnualWorkingTimeCalculation from './AnnualWorkingTimeCalculation'
import BankHolidayTreatment from './BankHolidayTreatment'
import MaxWeeklyHours from './MaxWeeklyHours'
import MaxYearlyHours from './MaxYearlyHours'
import WorkableDays from './WorkableDays'
import WorkingDayType from './WorkingDayType'
import WorkingHours from './WorkingHours'

interface Props {
  employeeId: string
  contractVersion: ContractVersionPageContractVersionFragment
  workingConditionVersionApplication?: WorkingConditionVersionApplicationFragment
  hasTimeBalance: boolean
  hasDevTimeTrackingWorkableDaysDefinition: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
}

const Time = ({
  employeeId,
  contractVersion,
  workingConditionVersionApplication,
  hasTimeBalance,
  hasDevTimeTrackingWorkableDaysDefinition,
  hasTimeTrackingAnnualisationDailyValue,
}: Props) => {
  const canEdit = contractVersion._policies?.edit?.can ?? false
  const canSeeWorkingDayType = contractVersion._policies?.seeWorkingDayType?.can ?? false
  const canSeeWorkingHours = contractVersion._policies?.seeWorkingHours?.can ?? false

  if (!canSeeWorkingDayType && !canSeeWorkingHours) return null

  const hasSpanishWorkingDayType =
    contractVersion.country === 'es' && !!contractVersion.spainWorkingDayType?.name
  const hasFrenchWorkingDayType =
    contractVersion.country === 'fr' && !!contractVersion.franceWorkingDayType?.name

  const hasWorkHours = !!contractVersion.workingHours
  const hasWorkingFrequency = !!contractVersion.workingHoursFrequency && hasWorkHours
  const hasWorkableDays = !!contractVersion.workingWeekDays
  const hasYearlyHours = !!contractVersion.maxLegalYearlyHours
  const hasWeeklyHours = !!contractVersion.maximumWeeklyHours
  const hasBankHolidayTreatment =
    hasDevTimeTrackingWorkableDaysDefinition && !!contractVersion.bankHolidayTreatment

  const showEmptySlate =
    canEdit &&
    !hasSpanishWorkingDayType &&
    !hasFrenchWorkingDayType &&
    !hasWorkingFrequency &&
    !hasWorkableDays &&
    !hasYearlyHours &&
    !hasWeeklyHours
  const workingHours = hasWorkHours ? centsToDecimal(contractVersion.workingHours ?? 0) : undefined
  const workingHoursFrequency = contractVersion.workingHoursFrequency
  const showWorkingDayType =
    !showEmptySlate &&
    canSeeWorkingDayType &&
    (canEdit || hasSpanishWorkingDayType || hasFrenchWorkingDayType)
  const showWorkingHours = !showEmptySlate && canSeeWorkingHours && (canEdit || hasWorkingFrequency)
  const showWorkableDays = !showEmptySlate && canSeeWorkingHours && (canEdit || hasWorkableDays)
  const showMaxYearlyHours =
    hasTimeBalance && !showEmptySlate && canSeeWorkingHours && (canEdit || hasYearlyHours)
  const showAnnualWorkingTimeCalculation =
    hasTimeTrackingAnnualisationDailyValue &&
    hasTimeBalance &&
    !showEmptySlate &&
    canSeeWorkingHours &&
    (canEdit || hasYearlyHours)
  const showMaxWeeklyHours =
    hasTimeBalance && !showEmptySlate && canSeeWorkingHours && (canEdit || hasWeeklyHours)
  const showBankHolidayTreatment = !showEmptySlate && canSeeWorkingHours && hasBankHolidayTreatment

  if (
    !showWorkingDayType &&
    !showWorkingHours &&
    !showWorkableDays &&
    !showMaxYearlyHours &&
    !showMaxWeeklyHours &&
    !showBankHolidayTreatment &&
    !showEmptySlate
  )
    return null

  return (
    <Section icon="clock" title={i18n.t('contract_versions.time.title')}>
      <Grid variant="fixed">
        {showWorkingDayType && (
          <WorkingDayType contractVersion={contractVersion} canEdit={canEdit} />
        )}
        {showWorkingHours && (
          <WorkingHours
            workingHours={workingHours}
            frequency={workingHoursFrequency ?? undefined}
          />
        )}
        {showWorkableDays && <WorkableDays workableDays={contractVersion.workingWeekDays ?? ''} />}
        {showMaxYearlyHours && (
          <MaxYearlyHours
            maxYearlyHoursInCents={contractVersion.maxLegalYearlyHours ?? 0}
            workingConditionVersionApplication={workingConditionVersionApplication}
          />
        )}
        {showMaxWeeklyHours && (
          <MaxWeeklyHours maxWeeklyHoursInCents={contractVersion.maximumWeeklyHours ?? 0} />
        )}
        {showBankHolidayTreatment && (
          <BankHolidayTreatment
            bankHolidayTreatment={contractVersion.bankHolidayTreatment ?? ''}
            workingConditionVersionApplication={workingConditionVersionApplication}
          />
        )}
        {showAnnualWorkingTimeCalculation && (
          <AnnualWorkingTimeCalculation
            annualWorkingTimeDistribution={
              contractVersion.annualWorkingTimeDistribution ?? undefined
            }
            workdaysCount={contractVersion.annualWorkdaysCount ?? undefined}
            adjustedDailyMinutes={contractVersion.adjustedDailyMinutes ?? undefined}
          />
        )}
        {showEmptySlate && !!contractVersion.id && (
          <BlankSection
            employeeId={employeeId}
            contractVersionId={contractVersion.id}
            icon="timer"
            title={i18n.t('contract_versions.time.blank_slate.title')}
            description={i18n.t('contract_versions.time.blank_slate.description')}
            buttonText={i18n.t('contract_versions.time.blank_slate.button')}
          />
        )}
      </Grid>
    </Section>
  )
}

export default observer(Time)
